import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, FileText, Loader2, RefreshCw, CheckCircle2, Landmark,
  CloudUpload, Download, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : null;
};

const STATUS_CLS = {
  draft: "border-zinc-500 text-zinc-400",
  approved: "border-amber-400/50 text-amber-300",
  fira_synced: "border-sky-400/50 text-sky-300",
  paid: "border-emerald-400/50 text-emerald-300",
};

export default function AdminSelfBilling() {
  const [month, setMonth] = useState(thisMonth());
  const [summary, setSummary] = useState(null);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        api.get("/admin/self-billing/summary", { params: { month } }),
        api.get("/admin/self-billing", { params: { month } }),
      ]);
      setSummary(s.data);
      setStatements(st.data || []);
    } catch (e) { toast.error(errMsg(e) || "Učitavanje nije uspjelo"); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(errMsg(e) || "Akcija nije uspjela"); }
    finally { setBusy(null); }
  };

  const toggleVat = (row) => act(`vat-${row.driver_id}`, () =>
    api.patch(`/admin/drivers/${row.driver_id}`, { is_vat_registered: !row.is_vat_registered }),
    "PDV status ažuriran");

  const generate = (row) => act(`gen-${row.driver_id}`, () =>
    api.post("/admin/self-billing/generate", { month, driver_id: row.driver_id }),
    "Obračun generiran");

  const openPdf = async (s) => {
    setBusy(`pdf-${s.id}`);
    try {
      const { data } = await api.get(`/admin/self-billing/${s.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF nije dostupan"); }
    finally { setBusy(null); }
  };

  const downloadSepa = async () => {
    setBusy("sepa");
    try {
      const { data } = await api.get("/admin/self-billing/sepa", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = `SEPA-payouts-${month}.xml`; a.click();
      toast.success("SEPA XML preuzet");
    } catch (e) {
      let msg = "SEPA export nije uspio";
      try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch { /* noop */ }
      toast.error(msg);
    } finally { setBusy(null); }
  };

  const syncAllFira = () => act("fira-bulk", async () => {
    const { data } = await api.post("/admin/self-billing/sync-fira-bulk", { month });
    const ok = data.results.filter((r) => r.success).length;
    toast.success(`Fira sync: ${ok}/${data.results.length} uspješno`);
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/5 bg-[#0a0a0a]/90 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/admin/payouts" data-testid="sb-back" className="p-2 rounded-full hover:bg-white/5 text-zinc-400">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Podizvođači · Samoizdavanje računa</p>
            <p className="text-sm font-medium">Self-Billing (čl. 79. st. 2.)</p>
          </div>
          <input type="month" data-testid="sb-month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-10 px-3 bg-black/40 border border-white/10 rounded-lg text-sm" />
          <button onClick={load} data-testid="sb-refresh" className="p-2.5 rounded-full hover:bg-white/5 text-zinc-400">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-[0.25em] text-zinc-400">Podizvođači — {month}</h2>
            {summary && <p className="text-xs text-zinc-500">PDV stopa sustava: {summary.vat_rate_pct}%</p>}
          </div>
          <div className="space-y-2">
            {(summary?.subcontractors || []).map((r) => (
              <div key={r.driver_id} data-testid={`sb-row-${r.driver_id}`} className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4 flex flex-wrap items-center gap-3">
                <div className="min-w-[220px] flex-1">
                  <p className="text-sm text-white">{r.name}{r.company_name ? <span className="text-zinc-500"> · {r.company_name}</span> : null}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {r.oib ? `OIB ${r.oib} · ` : ""}{r.iban ? `${r.iban}` : "bez IBAN-a"}
                  </p>
                  <span data-testid={`sb-agreement-${r.driver_id}`} className={`mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${r.self_billing_agreed ? "border-emerald-400/50 text-emerald-300" : "border-red-400/50 text-red-300"}`}>
                    {r.self_billing_agreed ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                    {r.self_billing_agreed ? "Ugovor prihvaćen" : "Ugovor NIJE prihvaćen"}
                  </span>
                </div>
                <div className="text-xs text-zinc-400 text-right">
                  <p>Bruto: <span className="text-zinc-200">{eur(r.gross_total)}</span> ({r.ride_count})</p>
                  <p>Provizija 20%: <span className="text-zinc-200">−{eur(r.commission_amount)}</span></p>
                  <p>Neto 80%: <span className="text-[#C5A059] font-medium">{eur(r.net_payout)}</span></p>
                </div>
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                  <input type="checkbox" data-testid={`sb-vat-toggle-${r.driver_id}`}
                    checked={r.is_vat_registered} onChange={() => toggleVat(r)}
                    disabled={busy === `vat-${r.driver_id}`} className="accent-[#C5A059]" />
                  U sustavu PDV-a
                </label>
                {r.statement ? (
                  <span className={`text-[9px] uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS_CLS[r.statement.status] || STATUS_CLS.draft}`}>
                    {r.statement.number} · {r.statement.status}
                  </span>
                ) : (
                  <button data-testid={`sb-generate-${r.driver_id}`} onClick={() => generate(r)}
                    disabled={busy === `gen-${r.driver_id}` || !r.ride_count || !r.self_billing_agreed}
                    title={!r.self_billing_agreed ? "Blokirano — ugovor nije prihvaćen" : ""}
                    className="h-9 px-4 rounded-full bg-[#C5A059] text-black text-[10px] uppercase tracking-widest font-semibold disabled:opacity-40 flex items-center gap-1.5">
                    {busy === `gen-${r.driver_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                    Generiraj obračun
                  </button>
                )}
              </div>
            ))}
            {!loading && !(summary?.subcontractors || []).length && (
              <p className="text-sm text-zinc-600">Nema vanjskih podizvođača.</p>
            )}
          </div>
        </section>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-xs uppercase tracking-[0.25em] text-zinc-400">Obračuni — {month}</h2>
            <div className="flex gap-2">
              <button data-testid="sb-fira-bulk" onClick={syncAllFira} disabled={busy === "fira-bulk"}
                className="h-9 px-4 rounded-full border border-sky-400/40 text-sky-300 text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                {busy === "fira-bulk" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CloudUpload className="w-3 h-3" />}
                Sync sve u Firu
              </button>
              <button data-testid="sb-sepa" onClick={downloadSepa} disabled={busy === "sepa"}
                className="h-9 px-4 rounded-full border border-[#C5A059]/50 text-[#C5A059] text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                {busy === "sepa" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                SEPA XML (pain.001)
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {statements.map((s) => (
              <div key={s.id} data-testid={`sb-stmt-${s.id}`} className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm text-white">
                    {s.number} · {s.company_name || s.driver_name}
                    <span data-testid={`sb-stmt-status-${s.id}`} className={`ml-2 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_CLS[s.status] || STATUS_CLS.draft}`}>{s.status}</span>
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {s.period_start} – {s.period_end} · {s.ride_count} vožnji · bruto {eur(s.gross_total)} · neto <span className="text-[#C5A059]">{eur(s.net_payout)}</span>
                    {s.is_vat_registered ? ` · PDV ${s.vat_rate_pct}% ${eur(s.vat_amount)}` : " · nije u sustavu PDV-a"}
                    {s.driver_accepted ? " · ✓ prihvatio izvođač" : ""}
                  </p>
                </div>
                <button data-testid={`sb-pdf-${s.id}`} onClick={() => openPdf(s)} disabled={busy === `pdf-${s.id}`}
                  className="h-8 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-[10px] uppercase tracking-widest flex items-center gap-1">
                  <FileText className="w-3 h-3" /> PDF
                </button>
                {s.status === "draft" && (
                  <button data-testid={`sb-approve-${s.id}`}
                    onClick={() => act(`ap-${s.id}`, () => api.post(`/admin/self-billing/${s.id}/approve`), "Odobreno")}
                    className="h-8 px-3 rounded-full border border-amber-400/40 text-amber-300 text-[10px] uppercase tracking-widest flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Odobri
                  </button>
                )}
                {s.status === "approved" && (
                  <button data-testid={`sb-fira-${s.id}`}
                    onClick={() => act(`fs-${s.id}`, async () => {
                      const { data } = await api.post(`/admin/self-billing/${s.id}/sync-fira`);
                      if (!data.success) throw { response: { data: { detail: data.error || "Fira greška" } } };
                    }, "Sinkronizirano u Firu")}
                    className="h-8 px-3 rounded-full border border-sky-400/40 text-sky-300 text-[10px] uppercase tracking-widest flex items-center gap-1">
                    <CloudUpload className="w-3 h-3" /> Fira
                  </button>
                )}
                {["approved", "fira_synced"].includes(s.status) && (
                  <button data-testid={`sb-paid-${s.id}`}
                    onClick={() => act(`pd-${s.id}`, () => api.post(`/admin/self-billing/${s.id}/mark-paid`), "Označeno plaćenim")}
                    className="h-8 px-3 rounded-full border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-widest flex items-center gap-1">
                    <Landmark className="w-3 h-3" /> Plaćeno
                  </button>
                )}
              </div>
            ))}
            {!loading && !statements.length && <p className="text-sm text-zinc-600">Nema obračuna za ovaj mjesec.</p>}
          </div>
        </section>
      </main>
    </div>
  );
}
